<template>
  <q-card flat bordered class="q-mb-md dedup-cluster">
    <q-card-section class="dedup-cluster__header row items-center justify-between">
      <div>
        <div class="text-subtitle2">
          {{ $t('composites.PartnerDedup.cluster.header', { count: cluster.losers.length }) }}
        </div>
        <div class="text-caption text-grey-6">
          {{ $t('composites.PartnerDedup.cluster.score_range', {
            min: cluster.min_score.toFixed(3),
            max: cluster.max_score.toFixed(3),
          }) }}
        </div>
      </div>
      <q-btn
        unelevated
        color="negative"
        :icon="symRoundedMergeType"
        :label="$t('composites.PartnerDedup.cluster.merge_selected', { count: selectedIds.length })"
        :disable="selectedIds.length === 0 || merging"
        :loading="merging"
        size="sm"
        @click="onMergeSelected"
      />
    </q-card-section>

    <q-banner
      class="dedup-cluster__scope q-mx-md q-mt-sm"
      :class="willBecomeShared(cluster.keeper) ? 'bg-amber-2 text-amber-10' : 'bg-blue-grey-1 text-blue-grey-9'"
      rounded
      dense
    >
      <template #avatar>
        <q-icon :name="symRoundedInfo" :color="willBecomeShared(cluster.keeper) ? 'amber-9' : 'blue-grey-7'" />
      </template>
      <div class="text-body2">
        <strong>{{ $t('composites.PartnerDedup.cluster.scope_summary_title') }}</strong>
        —
        <template v-if="!cluster.keeper.company_id">
          {{ $t('composites.PartnerDedup.cluster.scope_summary_keeper_shared') }}
        </template>
        <template v-else-if="willBecomeShared(cluster.keeper)">
          {{ $t('composites.PartnerDedup.cluster.scope_summary_will_demote', {
            current: cluster.keeper.company_name ?? '?',
          }) }}
        </template>
        <template v-else>
          {{ $t('composites.PartnerDedup.cluster.scope_summary_keeper_company', {
            name: cluster.keeper.company_name ?? '?',
          }) }}
        </template>
      </div>
    </q-banner>

    <q-banner
      v-if="cluster.suggest_supplier"
      class="bg-amber-2 text-amber-10 q-mx-md q-mt-sm"
      rounded
      dense
    >
      <template #avatar>
        <q-icon :name="symRoundedAutoFixHigh" color="amber-9" />
      </template>
      <div class="row items-center q-gutter-sm">
        <div class="col">
          <div class="text-weight-medium">
            {{ $t('composites.PartnerDedup.cluster.supplier_hint_title') }}
          </div>
          <div class="text-caption">
            {{ $t('composites.PartnerDedup.cluster.supplier_hint_body', { count: totalTransactions }) }}
          </div>
        </div>
        <q-toggle
          v-model="convertToSupplier"
          color="amber-9"
          :label="$t('composites.PartnerDedup.cluster.convert_to_supplier')"
        />
      </div>
    </q-banner>

    <table class="dedup-table">
      <thead>
        <tr>
          <th class="dedup-table__check">
            <q-checkbox :model-value="allSelected" @update:model-value="toggleAll" />
          </th>
          <th>{{ $t('composites.PartnerDedup.fields.name') }}</th>
          <th>{{ $t('composites.PartnerDedup.fields.legal_name') }}</th>
          <th>{{ $t('composites.PartnerDedup.fields.email') }}</th>
          <th>{{ $t('composites.PartnerDedup.fields.phone') }}</th>
          <th>{{ $t('composites.PartnerDedup.fields.billing_address') }}</th>
          <th class="text-right">{{ $t('composites.PartnerDedup.counts.transactions') }}</th>
          <th class="text-right">{{ $t('composites.PartnerDedup.counts.service_calls') }}</th>
          <th>{{ $t('composites.PartnerDedup.fields.last_transaction_at') }}</th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr class="dedup-table__keeper">
          <td class="dedup-table__check">
            <q-icon :name="symRoundedStar" size="18px" color="positive" />
          </td>
          <td>
            <div
              class="text-weight-bold"
              :class="winnerCellClass('scalar:name', cluster.keeper.id)"
            >{{ keeperDisplay('name') }}</div>
            <div v-if="overrideValue('scalar:name')" class="text-caption text-warning text-weight-medium">
              → {{ overrideValue('scalar:name') }}
            </div>
            <div v-else-if="autoFillPreview('name')" class="text-caption text-positive text-weight-medium">
              + {{ autoFillPreview('name') }}
            </div>
            <div class="text-caption text-grey-6">#{{ cluster.keeper.id.slice(0, 8) }}</div>
            <div class="q-mt-xs">
              <q-chip
                dense
                square
                size="sm"
                :color="scopeBadge(cluster.keeper).color"
                text-color="white"
                :label="scopeBadge(cluster.keeper).label"
              />
              <q-chip
                v-if="willBecomeShared(cluster.keeper)"
                dense
                square
                size="sm"
                color="amber-8"
                text-color="white"
                :label="$t('composites.PartnerDedup.cluster.will_become_shared')"
              />
            </div>
          </td>
          <td>
            <div :class="winnerCellClass('scalar:legal_name', cluster.keeper.id)">
              {{ keeperDisplay('legal_name') }}
            </div>
            <div v-if="overrideValue('scalar:legal_name')" class="text-caption text-warning text-weight-medium">
              → {{ overrideValue('scalar:legal_name') }}
            </div>
            <div v-else-if="autoFillPreview('legal_name')" class="text-caption text-positive text-weight-medium">
              + {{ autoFillPreview('legal_name') }}
            </div>
          </td>
          <td>
            <div :class="!cluster.keeper.default_email ? 'text-grey-5' : ''">
              {{ cluster.keeper.default_email || '—' }}
            </div>
            <div
              v-for="email in extraContactPreviews('default_email')"
              :key="email"
              class="text-caption text-positive text-weight-medium"
            >+ {{ email }}</div>
          </td>
          <td>
            <div :class="!cluster.keeper.default_phone ? 'text-grey-5' : ''">
              {{ cluster.keeper.default_phone || '—' }}
            </div>
            <div
              v-for="phone in extraContactPreviews('default_phone')"
              :key="phone"
              class="text-caption text-positive text-weight-medium"
            >+ {{ phone }}</div>
          </td>
          <td>
            <div :class="winnerCellClass('addresses:billing', cluster.keeper.id)">
              {{ formatAddress(cluster.keeper.billing_address) || '—' }}
            </div>
            <div v-if="overrideValue('addresses:billing')" class="text-caption text-warning text-weight-medium">
              → {{ overrideValue('addresses:billing') }}
            </div>
            <div v-else-if="autoFillPreview('billing_address')" class="text-caption text-positive text-weight-medium">
              + {{ autoFillPreview('billing_address') }}
            </div>
          </td>
          <td class="text-right text-weight-medium">{{ cluster.keeper.fk_counts.transactions }}</td>
          <td class="text-right text-weight-medium">{{ cluster.keeper.fk_counts.service_calls }}</td>
          <td>{{ formatDate(cluster.keeper.last_transaction_at) }}</td>
          <td>
            <q-chip dense square size="sm" color="positive" text-color="white" :label="$t('composites.PartnerDedup.cluster.keeper_badge')" />
          </td>
        </tr>

        <tr
          v-for="loser in cluster.losers"
          :key="loser.id"
          class="dedup-table__loser"
        >
          <td class="dedup-table__check">
            <q-checkbox v-model="selected[loser.id]" />
          </td>

          <td>
            <div class="dedup-cell" :class="cellClass(loser, 'name')">
              <span class="dedup-cell__value">{{ valueOf(loser, 'name') }}</span>
              <PickButton
                v-if="canPick(loser, 'name')"
                :active="winners['scalar:name'] === loser.id"
                @click="togglePick('scalar:name', loser.id)"
              />
            </div>
            <div class="text-caption text-grey-6">#{{ loser.id.slice(0, 8) }}</div>
            <div class="q-mt-xs">
              <q-chip
                dense
                square
                size="sm"
                :color="scopeBadge(loser).color"
                text-color="white"
                :label="scopeBadge(loser).label"
              />
            </div>
          </td>

          <td>
            <div class="dedup-cell" :class="cellClass(loser, 'legal_name')">
              <span class="dedup-cell__value">{{ valueOf(loser, 'legal_name') }}</span>
              <PickButton
                v-if="canPick(loser, 'legal_name')"
                :active="winners['scalar:legal_name'] === loser.id"
                @click="togglePick('scalar:legal_name', loser.id)"
              />
            </div>
          </td>

          <td :class="cellClass(loser, 'default_email')">{{ valueOf(loser, 'default_email') }}</td>
          <td :class="cellClass(loser, 'default_phone')">{{ valueOf(loser, 'default_phone') }}</td>

          <td>
            <div class="dedup-cell" :class="cellClass(loser, 'billing_address')">
              <span class="dedup-cell__value">{{ formatAddress(loser.billing_address) }}</span>
              <PickButton
                v-if="canPickAddress(loser)"
                :active="winners['addresses:billing'] === loser.id"
                @click="togglePick('addresses:billing', loser.id)"
              />
            </div>
          </td>

          <td class="text-right" :class="countClass(loser, 'transactions')">{{ loser.fk_counts.transactions }}</td>
          <td class="text-right" :class="countClass(loser, 'service_calls')">{{ loser.fk_counts.service_calls }}</td>
          <td :class="dateCellClass(loser, 'last_transaction_at')">{{ formatDate(loser.last_transaction_at) }}</td>
          <td class="dedup-table__actions">
            <q-btn
              flat
              dense
              size="sm"
              color="primary"
              :icon="symRoundedSettings"
              :label="$t('composites.PartnerDedup.cluster.review_pair')"
              @click="$emit('preview-merge', cluster.keeper.id, loser.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="dedup-cluster__hint text-caption text-grey-6">
      <q-icon :name="symRoundedInfo" size="14px" class="q-mr-xs" />
      {{ $t('composites.PartnerDedup.cluster.merge_help') }}
    </div>
  </q-card>
</template>

<script setup lang="ts">
import { computed, h, reactive, ref, watch } from 'vue'
import dayjs from 'dayjs'
import {
  symRoundedAutoAwesomeMotion as symRoundedAutoFixHigh,
  symRoundedCheckCircle,
  symRoundedInfo,
  symRoundedMergeType,
  symRoundedRadioButtonUnchecked,
  symRoundedSettings,
  symRoundedStar,
} from '@quasar/extras/material-symbols-rounded'
import { QBtn, QTooltip } from 'quasar'
import { useI18n } from 'vue-i18n'
import type { DuplicateCluster, FkCounts, PartnerSummary } from './usePartnerDedup'

const props = defineProps<{ cluster: DuplicateCluster }>()
const emit = defineEmits<{
  (
    e: 'merge',
    keeperId: string,
    loserIds: string[],
    fieldWinners: Record<string, string>,
    convertToSupplier: boolean,
  ): void
  (e: 'preview-merge', keeperId: string, loserId: string): void
}>()

const { t } = useI18n()

const selected = reactive<Record<string, boolean>>({})
const winners = reactive<Record<string, string>>({})
const merging = ref(false)
const convertToSupplier = ref(false)

const totalTransactions = computed(() =>
  Object.values(props.cluster.transaction_types).reduce((acc, n) => acc + Number(n), 0),
)

watch(
  () => props.cluster.suggest_supplier,
  (v) => {
    convertToSupplier.value = v
  },
  { immediate: true },
)

watch(
  () => props.cluster.losers.map((l) => l.id).join(','),
  () => {
    for (const k of Object.keys(selected)) delete selected[k]
    for (const k of Object.keys(winners)) delete winners[k]
    for (const l of props.cluster.losers) selected[l.id] = true
  },
  { immediate: true },
)

const selectedIds = computed(() => Object.keys(selected).filter((k) => selected[k]))
const allSelected = computed(
  () => props.cluster.losers.length > 0 && props.cluster.losers.every((l) => selected[l.id]),
)

function toggleAll(value: boolean) {
  for (const loser of props.cluster.losers) {
    selected[loser.id] = value
  }
}

function onMergeSelected() {
  if (!selectedIds.value.length) return
  merging.value = true
  try {
    emit(
      'merge',
      props.cluster.keeper.id,
      [...selectedIds.value],
      { ...winners },
      convertToSupplier.value,
    )
  } finally {
    merging.value = false
  }
}

type CellField = 'name' | 'legal_name' | 'default_email' | 'default_phone' | 'billing_address'

function valueOf(p: PartnerSummary, field: CellField): string {
  if (field === 'billing_address') return formatAddress(p.billing_address)
  const raw = (p as unknown as Record<string, unknown>)[field]
  return raw == null ? '' : String(raw)
}

function cellClass(loser: PartnerSummary, field: CellField): string {
  const lv = valueOf(loser, field).trim().toLowerCase()
  const kv = valueOf(props.cluster.keeper, field).trim().toLowerCase()
  if (!lv) return 'text-grey-6'
  if (!kv) return 'text-positive'
  return lv === kv ? 'text-grey-7' : 'text-warning text-weight-medium'
}

function countClass(loser: PartnerSummary, key: keyof FkCounts): string {
  const own = loser.fk_counts[key] ?? 0
  const keeper = props.cluster.keeper.fk_counts[key] ?? 0
  if (own === 0) return 'text-grey-6'
  if (own > keeper) return 'text-warning text-weight-bold'
  return 'text-grey-7'
}

function dateCellClass(loser: PartnerSummary, key: 'last_transaction_at' | 'updated_at'): string {
  const own = loser[key]
  const keeper = props.cluster.keeper[key]
  if (!own) return 'text-grey-6'
  if (!keeper) return 'text-positive'
  return dayjs(own).isAfter(dayjs(keeper)) ? 'text-warning text-weight-medium' : 'text-grey-7'
}

function canPick(loser: PartnerSummary, field: CellField): boolean {
  const lv = valueOf(loser, field).trim()
  const kv = valueOf(props.cluster.keeper, field).trim()
  return lv !== '' && lv.toLowerCase() !== kv.toLowerCase()
}

function canPickAddress(loser: PartnerSummary): boolean {
  const lv = formatAddress(loser.billing_address).trim()
  const kv = formatAddress(props.cluster.keeper.billing_address).trim()
  return lv !== '' && lv.toLowerCase() !== kv.toLowerCase()
}

function togglePick(field: string, loserId: string) {
  if (winners[field] === loserId) {
    delete winners[field]
  } else {
    winners[field] = loserId
  }
}

function winnerCellClass(field: string, partnerId: string): string {
  if (winners[field] === partnerId) return 'dedup-cell--winner'
  if (winners[field] && winners[field] !== partnerId && partnerId === props.cluster.keeper.id) {
    return 'dedup-cell--overridden'
  }
  return ''
}

function overrideValue(field: string): string {
  const winnerId = winners[field]
  if (!winnerId || winnerId === props.cluster.keeper.id) return ''
  const loser = props.cluster.losers.find((l) => l.id === winnerId)
  if (!loser) return ''
  if (field.startsWith('scalar:')) {
    const f = field.slice(7) as CellField
    return valueOf(loser, f)
  }
  if (field === 'addresses:billing') return formatAddress(loser.billing_address)
  return ''
}

type FillField = 'name' | 'legal_name' | 'invoicing_name' | 'billing_address'

function keeperDisplay(field: CellField): string {
  const v =
    field === 'billing_address'
      ? formatAddress(props.cluster.keeper.billing_address)
      : valueOf(props.cluster.keeper, field)
  return v.trim() === '' ? '—' : v
}

function autoFillPreview(field: FillField): string {
  // Only show auto-fill hint when keeper field is empty and no explicit pick is active.
  const keeperVal =
    field === 'billing_address'
      ? formatAddress(props.cluster.keeper.billing_address)
      : valueOf(props.cluster.keeper, field as CellField)
  if (keeperVal.trim() !== '') return ''

  const winnerKey = field === 'billing_address' ? 'addresses:billing' : `scalar:${field}`
  if (winners[winnerKey]) return ''

  for (const loser of props.cluster.losers) {
    if (!selected[loser.id]) continue
    const lv =
      field === 'billing_address'
        ? formatAddress(loser.billing_address)
        : valueOf(loser, field as CellField)
    if (lv.trim() !== '') return lv
  }
  return ''
}

function extraContactPreviews(field: 'default_email' | 'default_phone'): string[] {
  const keeperVal = (props.cluster.keeper[field] ?? '').toString().trim().toLowerCase()
  const seen = new Set<string>()
  if (keeperVal) seen.add(keeperVal)

  const out: string[] = []
  for (const loser of props.cluster.losers) {
    if (!selected[loser.id]) continue
    const lv = (loser[field] ?? '').toString().trim()
    if (!lv) continue
    const key = lv.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(lv)
  }
  return out
}

function scopeBadge(p: PartnerSummary): { color: string; label: string } {
  if (!p.company_id) {
    return { color: 'purple-7', label: t('composites.PartnerDedup.cluster.scope_shared') }
  }
  return {
    color: 'blue-7',
    label: t('composites.PartnerDedup.cluster.scope_company', { name: p.company_name ?? '?' }),
  }
}

function willBecomeShared(keeper: PartnerSummary): boolean {
  if (!keeper.company_id) return false
  return selectedIds.value.some((id) => {
    const loser = props.cluster.losers.find((l) => l.id === id)
    if (!loser) return false
    return !loser.company_id || loser.company_id !== keeper.company_id
  })
}

function formatAddress(address: unknown): string {
  if (!address || typeof address !== 'object') return ''
  const a = address as Record<string, string | null>
  const parts = [a.street_number, a.address, a.city, a.zip].filter((p) => !!p && String(p).trim() !== '')
  return parts.join(' ')
}

function formatDate(value: string | null) {
  return value ? dayjs(value).format('YYYY-MM-DD') : ''
}

const PickButton = (btnProps: { active: boolean; onClick: () => void }) =>
  h(
    QBtn,
    {
      flat: true,
      dense: true,
      round: true,
      size: 'xs',
      color: btnProps.active ? 'positive' : 'grey-6',
      icon: btnProps.active ? symRoundedCheckCircle : symRoundedRadioButtonUnchecked,
      class: 'dedup-cell__pick',
      onClick: btnProps.onClick,
    },
    () => h(QTooltip, () => t('composites.PartnerDedup.cluster.pick_winner_tooltip')),
  )
</script>

<style scoped>
.dedup-cluster {
  display: block;
  width: 100%;
}
.dedup-cluster__header {
  background: var(--q-grey-2);
  padding: 10px 16px;
}
.dedup-cluster__hint {
  padding: 8px 16px;
  background: var(--q-grey-1);
  border-top: 1px solid var(--q-grey-3);
}
.dedup-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.dedup-table th {
  text-align: left;
  padding: 8px 10px;
  font-weight: 500;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--q-grey-7);
  background: var(--q-grey-1);
  border-bottom: 1px solid var(--q-grey-4);
  white-space: nowrap;
}
.dedup-table td {
  padding: 10px;
  vertical-align: top;
  border-bottom: 1px solid var(--q-grey-3);
}
.dedup-table__check {
  width: 36px;
  padding-left: 12px !important;
}
.dedup-table__keeper {
  background: rgba(33, 186, 69, 0.08);
}
.dedup-table__keeper td:first-child {
  border-left: 4px solid var(--q-positive);
}
.dedup-table__loser:hover {
  background: rgba(0, 0, 0, 0.025);
}
.dedup-table__actions {
  white-space: nowrap;
  text-align: right;
}
.dedup-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.dedup-cell--winner {
  background: rgba(33, 186, 69, 0.18);
  border-radius: 4px;
  padding: 2px 6px;
  margin: -2px -6px;
}
.dedup-cell--overridden {
  text-decoration: line-through;
  text-decoration-color: var(--q-warning);
  opacity: 0.6;
  display: inline-block;
}
.dedup-cell__pick {
  opacity: 0.4;
  transition: opacity 0.1s ease;
}
.dedup-cell:hover .dedup-cell__pick,
.dedup-cell--winner .dedup-cell__pick {
  opacity: 1;
}
:global(.body--dark) .dedup-cluster__header,
:global(.body--dark) .dedup-table th {
  background: rgba(255, 255, 255, 0.04);
}
:global(.body--dark) .dedup-cluster__hint {
  background: rgba(255, 255, 255, 0.02);
}
:global(.body--dark) .dedup-table td,
:global(.body--dark) .dedup-table th {
  border-color: rgba(255, 255, 255, 0.08);
}
:global(.body--dark) .dedup-table__loser:hover {
  background: rgba(255, 255, 255, 0.04);
}
</style>
